/**
 * La marca del club, derivada · `BFF_BO.md` §9 · `BFF_APPs.md` §9
 *
 * **Compartido por el panel y las Apps** desde el 26-09-2026. Vivía en
 * `panel/src/shared/ui/marca.ts`, y al llegar las Apps la alternativa era copiarlo:
 * dos sitios que calculan el color del club y que un día, tras un arreglo en uno
 * solo, enseñan dos colores distintos para el mismo club.
 *
 * Aquí está la aritmética —contraste, oscurecer, aclarar, la paleta derivada— y
 * nada de cómo se pinta: el panel la vuelca en variables CSS y las Apps en un
 * objeto de estilos de React Native.
 *
 * Se guarda **un color y un logo**. La escala se deriva al pintar, y esa decisión
 * hay que tomarla el primer día porque rehacerlo después es carísimo:
 *
 * - **Ningún componente lleva un color escrito a mano.** Todo sale de variables.
 *   El día que mejoremos este algoritmo, mejoran los 200 clubes a la vez.
 * - **La paleta derivada no se guarda.** Una paleta guardada es una paleta
 *   congelada.
 * - **Contraste AA**: 4,5:1 en texto, 3:1 en interfaz. Algún club elegirá un
 *   amarillo, y hay que oscurecerlo hasta cumplir en vez de rechazarlo.
 *
 * Lo que **no** se tiñe nunca, y está aquí escrito para que no se olvide:
 * los colores de estado (verde pagado, rojo impagado, ámbar pendiente) porque es
 * dinero, y los colores de cinturón, que vienen de `/belt-schemes/{id}/ranks` y
 * son **dato de dominio**: el verde del cinturón verde es verde en todos los
 * clubes del mundo. Teñirlo haría que la interfaz mintiera sobre el grado.
 */

/**
 * Lo que devuelve `GET /clubs/{club}/branding`, en lo que usa el tema.
 *
 * `brand_contrast.text_color` **lo decide el servidor**, y aquí no se recalcula:
 * la comprobación de contraste del backend resuelve en vez de rechazar —un color
 * claro no da `422`, da `text_color: '#111111'`—. Recalcularlo en el cliente
 * sería la misma trampa que tenían los precios en dos sitios: dos sitios que un
 * día discrepan.
 */
export interface Marca {
  /**
   * **Puede ser `null`, y es el caso normal el primer día.**
   *
   * El contrato lo dice —`brand_color: Union([Color, Null])`— y este tipo decía
   * `string`. Un club recién dado de alta no ha elegido color, así que llega
   * `null` y `esColorValido` lo pasaba por `.trim()`: **el panel entero se caía
   * el primer día de cada cliente nuevo**, con «Cannot read properties of null».
   *
   * No salía en desarrollo porque la base de siempre arrastraba un color puesto
   * hacía meses; apareció al recrearla desde el seed, el 16-09-2026.
   */
  readonly brand_color: string | null;
  readonly brand_contrast?: { readonly text_color: string } | undefined;
  /** El de fondo claro, el de la cabecera en modo claro. */
  readonly logo_asset_id?: string | null | undefined;
  /** El de fondo oscuro, el de la cabecera en modo oscuro. Opcional. */
  readonly logo_dark_asset_id?: string | null | undefined;
}

/** sRGB 0-255. */
interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

const CANONICO = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** El verde de Kiap, para cuando el club todavía no ha elegido. */
export const COLOR_POR_DEFECTO = '#1b5e20';

/** Sin color no hay color válido: `null` es «todavía no ha elegido», no un error. */
export function esColorValido(hex: string | null | undefined): boolean {
  return typeof hex === 'string' && CANONICO.test(hex.trim());
}

export function aRgb(hex: string): Rgb {
  const limpio = hex.trim().replace('#', '');
  const largo =
    limpio.length === 3
      ? limpio
          .split('')
          .map((c) => c + c)
          .join('')
      : limpio;

  return {
    r: parseInt(largo.slice(0, 2), 16),
    g: parseInt(largo.slice(2, 4), 16),
    b: parseInt(largo.slice(4, 6), 16),
  };
}

const aHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b]
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;

/**
 * Luminancia relativa, WCAG 2.1.
 *
 * No es el brillo ingenuo `(r+g+b)/3`: el ojo humano ve el verde mucho más claro
 * que el azul, y con la media un azul oscuro y un verde oscuro salen iguales
 * cuando uno cumple el contraste y el otro no.
 */
export function luminancia(hex: string): number {
  const { r, g, b } = aRgb(hex);

  const canal = (valor: number): number => {
    const v = valor / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/** Razón de contraste WCAG entre dos colores. De 1 (idénticos) a 21. */
export function contraste(a: string, b: string): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  const [claro, oscuro] = la > lb ? [la, lb] : [lb, la];
  return (claro! + 0.05) / (oscuro! + 0.05);
}

const mezclar = (hex: string, hacia: Rgb, cantidad: number): string => {
  const c = aRgb(hex);
  return aHex({
    r: c.r + (hacia.r - c.r) * cantidad,
    g: c.g + (hacia.g - c.g) * cantidad,
    b: c.b + (hacia.b - c.b) * cantidad,
  });
};

const NEGRO: Rgb = { r: 0, g: 0, b: 0 };
const BLANCO: Rgb = { r: 255, g: 255, b: 255 };

/**
 * Oscurece un color hasta que contrasta lo pedido contra blanco.
 *
 * Es lo que salva al club que elige un amarillo: en vez de rechazarle su color
 * —que es su marca y tiene derecho a ella— se usa una variante suya más oscura
 * para el texto y los bordes. Devuelve el original si ya cumple.
 */
export function oscurecerHastaContrastar(hex: string, minimo: number): string {
  if (contraste(hex, '#ffffff') >= minimo) return hex;

  // Pasos de un 4 %: fino para no ennegrecer de golpe un color que le faltaba
  // poco, y acotado para no quedarse dando vueltas.
  for (let paso = 1; paso <= 25; paso += 1) {
    const candidato = mezclar(hex, NEGRO, paso * 0.04);
    if (contraste(candidato, '#ffffff') >= minimo) return candidato;
  }

  // Ni el negro contrasta 4,5:1 contra blanco por debajo de… bueno, sí lo hace.
  // Este retorno es la red: un color imposible acaba en negro, que siempre vale.
  return '#000000';
}

/**
 * La superficie del modo oscuro, contra la que se mide el contraste allí. La misma
 * que `--superficie` en `tema.css`: si cambia una, cambia la otra.
 */
export const SUPERFICIE_OSCURA = '#171b21';

const FONDO_OSCURO = aRgb(SUPERFICIE_OSCURA);

/**
 * Aclara un color hasta que contrasta lo pedido contra la superficie oscura.
 *
 * El espejo de `oscurecerHastaContrastar`, para el modo oscuro: el azul marino de
 * un club, perfecto para un enlace sobre blanco, es invisible sobre casi negro. Se
 * usa una variante suya más clara, igual que en claro se usa una más oscura.
 */
export function aclararHastaContrastar(hex: string, minimo: number): string {
  if (contraste(hex, SUPERFICIE_OSCURA) >= minimo) return hex;

  for (let paso = 1; paso <= 25; paso += 1) {
    const candidato = mezclar(hex, BLANCO, paso * 0.04);
    if (contraste(candidato, SUPERFICIE_OSCURA) >= minimo) return candidato;
  }

  return '#ffffff';
}

/**
 * El texto que se lee **encima** del color del club.
 *
 * Solo como red por si `brand_contrast` no viniera: la respuesta del servidor
 * manda. Se deja porque el tema tiene que pintar algo legible incluso con una
 * respuesta antigua en caché, no para tomar la decisión.
 */
export function textoSobre(hex: string): string {
  return contraste(hex, '#ffffff') >= 4.5 ? '#ffffff' : '#111111';
}

/** Los tonos de la marca de un club, ya derivados para un aspecto. */
export interface PaletaDeMarca {
  /** El color del club tal cual: fondo de los botones principales. */
  readonly base: string;
  /** Para texto y enlaces: contrasta 4,5:1 con el fondo del aspecto. */
  readonly texto: string;
  /** Para bordes, iconos y el filo del foco: 3:1. */
  readonly interfaz: string;
  /** El texto encima de `base`. Lo decide el servidor si lo mandó. */
  readonly contraste: string;
  /** Tinte muy suave: fondo de lo seleccionado. */
  readonly suave: string;
  readonly borde: string;
  /** El botón pulsado. */
  readonly hundido: string;
}

/**
 * La paleta del club para un aspecto.
 *
 * En oscuro, lo mismo al revés: el texto y los bordes se aclaran hasta contrastar
 * con la superficie oscura, los tintes se mezclan hacia ella y el «hundido» sube
 * de luz en vez de bajar. El botón sigue siendo el color del club tal cual, con el
 * texto que dijo el servidor.
 */
export function paletaDeMarca(
  marca: Marca,
  aspecto: 'claro' | 'oscuro' = 'claro'
): PaletaDeMarca {
  const base = esColorValido(marca.brand_color)
    ? marca.brand_color!.trim().toLowerCase()
    : COLOR_POR_DEFECTO;
  const contraste = marca.brand_contrast?.text_color ?? textoSobre(base);

  if (aspecto === 'oscuro') {
    return {
      base,
      texto: aclararHastaContrastar(base, 4.5),
      interfaz: aclararHastaContrastar(base, 3),
      contraste,
      suave: mezclar(base, FONDO_OSCURO, 0.82),
      borde: mezclar(base, FONDO_OSCURO, 0.5),
      hundido: mezclar(base, BLANCO, 0.15),
    };
  }

  // Dos mínimos distintos y no es un descuido: 4,5:1 es texto (WCAG AA) y 3:1 es
  // interfaz —bordes, iconos, el filo de un campo enfocado—.
  return {
    base,
    texto: oscurecerHastaContrastar(base, 4.5),
    interfaz: oscurecerHastaContrastar(base, 3),
    contraste,
    suave: mezclar(base, BLANCO, 0.88),
    borde: mezclar(base, BLANCO, 0.62),
    hundido: mezclar(base, NEGRO, 0.18),
  };
}
